
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { PackageItem } from '@/types/package-item';

const fetchExclusions = async () => {
  const { data, error } = await supabase
    .from('package_exclusions')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return data;
};

const addExclusion = async (item: Pick<PackageItem, 'text_ar' | 'text_en'>) => {
  const { data, error } = await supabase
    .from('package_exclusions')
    .insert([item])
    .select();
  if (error) throw new Error(error.message);
  return data[0];
};

const updateExclusion = async (item: Pick<PackageItem, 'id' | 'text_ar' | 'text_en'>) => {
  const { id, ...updateData } = item;
  const { data, error } = await supabase
    .from('package_exclusions')
    .update({ ...updateData, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select();
  if (error) throw new Error(error.message);
  return data[0];
};

const deleteExclusion = async (id: string) => {
  const { error } = await supabase
    .from('package_exclusions')
    .delete()
    .eq('id', id);
  if (error) throw new Error(error.message);
  return id;
};

export const usePackageExclusions = () => {
  const queryClient = useQueryClient();

  const { data: exclusions, isLoading, error } = useQuery<PackageItem[]>({
    queryKey: ['package_exclusions'],
    queryFn: fetchExclusions,
  });

  const mutationOptions = {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['package_exclusions'] });
    },
  };

  const addMutation = useMutation({
    mutationFn: addExclusion,
    ...mutationOptions,
  });

  const updateMutation = useMutation({
    mutationFn: updateExclusion,
    ...mutationOptions,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteExclusion,
    ...mutationOptions,
  });

  return {
    exclusions,
    isLoading,
    error,
    addExclusion: addMutation.mutateAsync,
    updateExclusion: updateMutation.mutateAsync,
    deleteExclusion: deleteMutation.mutateAsync,
    isAdding: addMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
